import React, { useEffect, useMemo, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import defaultImage from "../../../src/Assets/fallbackimg_giftcard.png";
import pointIcon from "../../../src/Assets/point_icon.svg"
import { useSelector } from "react-redux";
import getTranslationData from "../../TranslationUtility/TranslationUtility";
import {
  ClientInfo,
  getImageUrl,
  trackEvent,
  useGaTrackingConfig,
  getRewardBaseDenomination,
  calculateEarning,
  EARNING_DISPLAY_TYPE,
  useEarnRoiCalculationContext,
  formatPrice,
} from "../Utility/Utility";
import { encryptURLParam } from "../../Common/Axios/encrypt";
import {
  getMinimumEnabledDenomination,
  getRewardRoundingSettings,
} from "../Card/utils/rewardRounding";

const isProductStockedOut = (item) =>
  Array.isArray(item?.clientProductDenomination) &&
  item.clientProductDenomination.every(
    (denomination) => denomination?.stockOut === true,
  );

const moveStockedOutProductsToEnd = (products = []) => {
  const availableProducts = [];
  const stockedOutProducts = [];

  products.forEach((item) => {
    if (isProductStockedOut(item)) stockedOutProducts.push(item);
    else availableProducts.push(item);
  });

  const orderedStockedOutProducts = stockedOutProducts.sort((a, b) => {
    const aOrder = Number(a?.displayOrder);
    const bOrder = Number(b?.displayOrder);
    const normalizedA = Number.isFinite(aOrder)
      ? aOrder
      : Number.MAX_SAFE_INTEGER;
    const normalizedB = Number.isFinite(bOrder)
      ? bOrder
      : Number.MAX_SAFE_INTEGER;

    return normalizedA - normalizedB;
  });

  return [...availableProducts, ...orderedStockedOutProducts];
};

const TrendingGiftCardOptimized = ({
  product,
  handleShow,
  productValues,
  setPageNumber,
  totalCount,
}) => {
  const navigate = useNavigate();
  // const [notFoundStates, setNotFoundStates] = useState([]);
  const translation = getTranslationData();
  const [stockOutDenominations, setStockOutDenominations] = useState([]);
  const gaTrackingConfig = useGaTrackingConfig();
  const clientConfigurationData = useSelector(
    (state) => state.clientConfigurationReducer.clientConfigurationData?.[0]
  );
  const rewardRoiCalculationContext = useEarnRoiCalculationContext();
  const loaderRef = useRef(null);
  const [hasMore, setHasMore] = useState(true);
  const orderedProducts = useMemo(
    () => moveStockedOutProductsToEnd(Array.isArray(product) ? product : []),
    [product],
  );
  // id -> first matching product (same result as product.find), built once per
  // product list instead of a linear scan for every card on every render.
  const productById = useMemo(() => {
    const byId = new Map();
    if (Array.isArray(product)) {
      product.forEach((entry) => {
        if (entry && !byId.has(entry.id)) byId.set(entry.id, entry);
      });
    }
    return byId;
  }, [product]);
  const earnLabelConfig = clientConfigurationData?.clientConfig?.find(
    (item) => item.configKey.toLowerCase() === "isearnlabelshowonly"
  )?.configValue;
  const isDiscountShowLabel = clientConfigurationData?.clientConfig?.find(
    (item) => item.configKey.toLowerCase() === "isdiscountlabeldisplay"
  )?.configValue;
  const isDiscountShowLabelConfig = isDiscountShowLabel === "1" || isDiscountShowLabel === 1 || isDiscountShowLabel === "true" || isDiscountShowLabel === true;
  const isEarnLabelConfig = earnLabelConfig === "1" || earnLabelConfig === 1 || earnLabelConfig === "true" || earnLabelConfig === true;

  useEffect(() => {
    if (totalCount && product) {
      setHasMore(product.length < totalCount);
    } else if (totalCount === 0) {
      setHasMore(false);
    }
  }, [product?.length, totalCount]); // Added totalCount dependency

  // IntersectionObserver for infinite scroll
  useEffect(() => {
    if (!hasMore || !product || product.length >= totalCount) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          hasMore &&
          product.length < totalCount
        ) {
          setPageNumber((prev) => prev + 1);
        }
      },
      {
        root: null,
        rootMargin: "300px",
        threshold: 0.1,
      }
    );

    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }

    return () => {
      if (loaderRef.current) {
        observer.unobserve(loaderRef.current);
      }
      observer.disconnect();
    };
  }, [hasMore, setPageNumber, product, totalCount]);

  const handleSubmit = (item, index) => {
    sessionStorage.setItem("ga4_seletedProductIndex", index + 1);
    const productId = item?.id?.toString();
    const finalProductId =
      process.env.REACT_APP_IS_E_E === "false"
        ? productId
        : encryptURLParam(productId);


    trackEvent("select_item", {
      page_url: window.location.href,
      page_type: "listing_page",
      client_code: gaTrackingConfig.trackingContext.client_code,
      platform_code: gaTrackingConfig.trackingContext.platform_code,
      ecommerce: {
        items: [
          {
            item_id: item?.sku,
            item_name: item?.name,
            item_brand: item?.name,
            item_category: 'Giftcards',
            item_category2: item?.clientCategoryName || "",
            affiliation: gaTrackingConfig.trackingContext.affiliation,
            item_list_name: sessionStorage.getItem("ga4_item_list_name") ?
              sessionStorage.getItem("ga4_item_list_name") : "products",
            item_list_id: sessionStorage.getItem("ga4_item_list_name") ?
              sessionStorage.getItem("ga4_item_list_name") : "products",
            item_image: getImageUrl(item?.small),
            item_url: item?.url ? item?.url : "NA",
            index: parseInt(sessionStorage.getItem("ga4_seletedProductIndex")) || 0,
          }]
      }
    });
    clientConfigurationData?.enableQuickBy
      ? handleShowModal(item)
      : navigate(`/giftcard?productId=${finalProductId}`, { state: { item } });
  };
  // const productDiscount = useSelector(
  //   (state) =>
  //     state?.ClientProductMappingbyIdReducer.data?.[0]?.clientProductMapping
  // );

  // const getMaxPrice = (priceDenominations) => {
  //   if (priceDenominations) {
  //     const pricesArray = priceDenominations?.split(",");
  //     const pricesNumbers = pricesArray?.map(Number);
  //     return Math.max(...pricesNumbers);
  //   }
  // };
  // const getMinPrice = (priceDenominations) => {
  //   if (priceDenominations) {
  //     const pricesArray = priceDenominations?.split(",");
  //     const pricesNumbers = pricesArray?.map(Number);

  //     return Math.min(...pricesNumbers);
  //   }
  // };

  const calculatePointsern = (item, isString) => {
    const productId = item.id;
    const prices = item?.clientProductDenomination
      ?.map((denomination) => Number(denomination?.denomination))
      .filter((price) => Number.isFinite(price) && price > 0) || [];
    const enabledPrices = item?.clientProductDenomination
      ?.filter((denomination) => denomination?.enabled === true)
      .map((denomination) => Number(denomination?.denomination))
      .filter((price) => Number.isFinite(price) && price > 0) || [];
    const maxPrice = enabledPrices.length ? Math.max(...enabledPrices) : 0;
    const minPrice = prices.length ? Math.min(...prices) : 0;
    if (minPrice <= 0 || maxPrice <= 0) {
      return null;
    }
    const discountEntry = productById.get(productId);
    if (discountEntry?.minEarnPoints > 0) {
      const earnUpToPoints = Math.round(
        (discountEntry.minEarnPoints / minPrice) *
        (maxPrice - (maxPrice * discountEntry.customerDiscount) / 100)
      );
      return isString
        ? earnUpToPoints.toLocaleString("en-IN")
        : earnUpToPoints;
    }
    return null;
  };

  const calculatePoints = (item, isString) => {
    const productId = item.id;
    const minPrice = getMinimumDenominationFallback(
      item?.clientProductDenomination,
    );
    const startingPrice =
      getMinimumEnabledDenomination(item?.clientProductDenomination)
      || minPrice;
    if (minPrice <= 0 || startingPrice <= 0) {
      return null;
    }
    const discountEntry = productById.get(productId);

    if (discountEntry) {
      if (discountEntry.maxEarnPoints > 0) {
        // apply discount
        let discountedPrice =
          startingPrice
          - (startingPrice * discountEntry.customerDiscount) / 100;

        // apply convenience fee
        const convenienceFeeAmount =
          (discountedPrice * (discountEntry.convenienceFee || 0)) / 100;

        // apply gst
        const gstAmount =
          (convenienceFeeAmount * (discountEntry.gst || 0)) / 100;
        const finalPrice =
          discountedPrice + convenienceFeeAmount + gstAmount;
        const roundingSettings = getRewardRoundingSettings(
          clientConfigurationData?.pointRoundingConfiguration,
        );
        const points = calculateEarning({
          ...rewardRoiCalculationContext,
          amount: finalPrice,
          minBurnPoints: discountEntry.maxEarnPoints,
          baseDenomination: minPrice,
          burnRoundMethod: roundingSettings?.roundMethod ?? 5,
          burnRoundDigits: roundingSettings?.roundDigits ?? 0,
        }).pointsRequired;

        return isString ? `${points} ${translation?.pts}` : points;
      }
    }
    return null;
  };

  const calculateDiscount = (item) => {
    // Starting price is the minimum enabled denomination.
    const minimumEnabledPrice = getMinimumEnabledDenomination(
      item?.clientProductDenomination,
    );
    if (minimumEnabledPrice > 0) {
      return minimumEnabledPrice;
    }

    return getMinimumDenominationFallback(item?.clientProductDenomination) || null;
  };

  // Starting price for display, with thousands separators; empty when no price exists.
  const formatStartingPrice = (item) => {
    const price = calculateDiscount(item);
    return price ? formatPrice(price) : price;
  };
  const handleShowModal = (item) => {
    handleShow(false);
    productValues(item);
  };

  // const handleImageError = (index) => {
  //   setNotFoundStates((prevState) => {
  //     const newState = [...prevState];
  //     newState[index] = true;
  //     return newState;
  //   });
  // };

  const imageError = (e) => {
    e.target.onerror = null; // Prevent infinite loop if fallback fails
    e.target.src = defaultImage; // Set fallback
  };

  const [nav, setNav] = useState();
  const isStartingPrice = clientConfigurationData?.clientConfig?.find(item => item.configKey === "startingPrice")

  const shouldForceShowStartingFrom = isStartingPrice?.configValue === "1";
  // const categoryAllData = useSelector(
  //   (state) => state.userRoleSliceReducer.categoryData
  // );

  useEffect(() => {
    setNav(clientConfigurationData?.enableQuickBy);

  }, [clientConfigurationData]);
  useEffect(() => {
    if (orderedProducts) {
      const itemStockOut = orderedProducts.map(item =>
        item?.clientProductDenomination?.every(denom => denom.stockOut === true) || false
      );
      setStockOutDenominations(itemStockOut || []);
    }

  }, [orderedProducts]);
  const clientinfo = ClientInfo();
  const clientConfigurations = clientinfo?.clientData?.clientConfigurations;
  const isEarnView = clientConfigurations?.find(
    (item) => item.configKey.toLowerCase() === "isearnview"
  )?.configValue;
  const isBurn = clientConfigurations?.find(
    (item) => item.configKey.toLowerCase() === "isburn"
  )?.configValue;
  const isEarn = clientConfigurations?.find(
    (item) => item.configKey.toLowerCase() === "isearn"
  )?.configValue;
  const getMinimumDenominationFallback = (denominations) => {
    const values = (Array.isArray(denominations) ? denominations : [])
      .map((denomination) => {
        const amount = Number(
          denomination?.denomination ?? denomination?.price ?? denomination?.value,
        );
        return Number.isFinite(amount) && amount > 0 ? amount : null;
      })
      .filter((value) => value !== null);

    return values.length ? Math.min(...values) : 0;
  };
  const productList = Array.isArray(orderedProducts)
    ? orderedProducts.map((item, index) => (

      <div
        key={index}
        className="product_list_card card_mob_p custom-col position-relative"

      >
        <div className={`giftcardbox feat-gift-card-sz1 ${stockOutDenominations[index] ? 'stockoutbackdrop' : ''}`} >
          {stockOutDenominations[index] &&
            <div className="stockout-image-container"><div className="stockout-image stockout-top-27"><span className="stockout-text">{translation?.stock_out}</span></div></div>
          }
          <div onClick={
            stockOutDenominations[index]
              ? undefined
              : () => handleSubmit(item, index)
          }>
            {/* {notFoundStates[index] && (
              <div className="info-icon">
                <i className="fa fa-info-circle" aria-hidden="true"></i>
                <div>{translation?.errorInImagePath}</div>
              </div>
            )} */}
            {/* {item?.customerDiscount !== 0 && (
              <div className="position-absolute top-1 start-50 translate-middle-x discountOff2">
                {item?.customerDiscount}% {translation?.off}
              </div>
            )} */}
            <div className="giftcard_img_box">
              <img
                // src={notFoundStates[index] ? defaultImage : item?.small}
                src={getImageUrl(item?.small)}
                onError={(e) => {
                  //handleImageError(index);
                  imageError(e);
                }}
                alt={item?.name}
                className="w-100"
              />
            </div>
            <h4
              className={`card-name ${clientConfigurationData?.showVoucherNameInLine === 2
                ? "eclips_line-two"
                : "eclips_line-one"
                }`}
              title={item?.name}
            >
              {item?.name}
              {/* {item?.name?.length >= 20
                ? item?.name?.substring(0, 50) + "..."
                : item?.name} */}
            </h4>
            {item?.customerDiscount !== 0 && (
              <div className={isDiscountShowLabelConfig ? "discountOff2 " : "d-none"}>
                {item?.customerDiscount}% {translation?.off}
              </div>
            )}
          </div>
          <div className="fixFromBottom">
            {/* Always show starting price if shouldForceShowStartingFrom is enabled */}
            {shouldForceShowStartingFrom && (
              <span className={"aapoForMob trend-mb"}>
                <Link
                  to="#"
                  data-bs-toggle="modal"
                  data-bs-target="#earnpoints"
                  className="start-point1"
                  onClick={(e) => {
                    e.preventDefault();
                    nav ? handleShowModal(item, index) : handleSubmit(item, index);
                  }}
                >
                  {translation?.startingFrom}
                  <span className="startingPricesBold">{" "}₹{formatStartingPrice(item)}</span>
                </Link>
              </span>
            )}
            {(() => {
              // Global config: Earn is enabled only when BOTH isEarnView AND isEarn are "1"
              const isEarnEnabled = isEarnView === "1" && isEarn === "1";
              // Global config: Burn is enabled only when isBurn is "1"
              const isBurnEnabled = isBurn === "1";

              // Product-level: check if this product has earn/burn points configured
              const hasProductEarn = isEarnEnabled && !!calculatePointsern(item, false);
              const hasProductBurn = isBurnEnabled && !!calculatePoints(item, false);

              // CASE 1: Both earn and burn are configured at product level
              if (hasProductEarn && hasProductBurn && !isEarnLabelConfig) {
                return (
                  <>
                    {/* Buy and Earn */}
                    <span className={`aapoForMob trend-mb earn_upto_points_icon`}>
                      <Link
                        to="#"
                        data-bs-toggle="modal"
                        data-bs-target="#earnpoints"
                        className="earn-point1"
                        onClick={(e) => {
                          e.preventDefault();
                          nav ? handleShowModal(item, index) : handleSubmit(item, index);
                        }}
                      >
                        {translation?.buyAndEarnUpTo}{" "}
                        {/* <span className="pts-col">
                          <span className="pts_text">{translation?.pts}</span>{" "}
                          <img src={pointIcon} alt="point" className="point_icon" />{" "}
                          {calculatePointsern(item, true)}
                        </span> */}
                        <span className="pts-col">
                          <img src={pointIcon} alt="point" className="point_icon" />{" "}
                          {calculatePointsern(item, true)} <span className="pts_text">{translation?.pts}</span>
                        </span>
                      </Link>
                    </span>
                    {/* Or Redeem From (both configured so use "orRedeemFrom") */}
                    <span className={isEarnLabelConfig ? "d-none" : "earn_upto_points trend-mb"}>
                      <span>{translation?.orRedeemFrom} </span>
                      <span className="pts-col">
                        <img src={pointIcon} alt="point" className="point_icon" />{" "}
                        {calculatePoints(item, false)?.toLocaleString("en-IN")} <span className="pts_text">{translation?.pts}</span>
                      </span>
                    </span>
                  </>
                );
              }

              // CASE 2: Only earn is configured at product level
              if (hasProductEarn && !hasProductBurn && !isEarnLabelConfig) {
                return (
                  <span className={"aapoForMob trend-mb m-b-2-5"}>
                    <Link
                      to="#"
                      data-bs-toggle="modal"
                      data-bs-target="#earnpoints"
                      className="earn-point1"
                      onClick={(e) => {
                        e.preventDefault();
                        nav ? handleShowModal(item, index) : handleSubmit(item, index);
                      }}
                    >
                      {translation?.buyAndEarnUpTo}{" "}
                      {calculatePointsern(item, true)}
                    </Link>
                  </span>
                );
              }

              // CASE 3: Only burn is configured at product level
              if (!hasProductEarn && hasProductBurn && !isEarnLabelConfig) {
                return (
                  <span className={isEarnLabelConfig ? "d-none" : "earn_upto_points trend-mb m-b-2"}>
                    <span>{translation?.redeemFrom} </span>
                    <span className="pts-col">
                      <img src={pointIcon} alt="point" className="point_icon" />{" "}
                      {calculatePoints(item, false)?.toLocaleString("en-IN")} <span className="pts_text">Pts</span>
                    </span>
                  </span>
                );
              }

              // CASE 4: Neither earn nor burn configured — show starting price (if not already forced)
              if (!shouldForceShowStartingFrom && !isEarnLabelConfig) {
                return (
                  <span className={"aapoForMob trend-mb m-b-2-5"}>
                    <Link
                      to="#"
                      data-bs-toggle="modal"
                      data-bs-target="#earnpoints"
                      className="start-point1"
                      onClick={(e) => {
                        e.preventDefault();
                        nav ? handleShowModal(item, index) : handleSubmit(item, index);
                      }}
                    >
                      {translation?.startingFrom}
                      <span className="startingPricesBold">{" "}₹{formatStartingPrice(item)}</span>
                    </Link>
                  </span>
                );
              }
              // CASE 5: Neither earn nor burn configured — show starting price (if not already forced)
              if (isEarnLabelConfig) {
                const denominationConfigs = Array.isArray(item?.clientProductDenomination)
                  ? item.clientProductDenomination
                  : [];

                const baseDenomination = getRewardBaseDenomination({
                  product: item,
                  eligibleDenominations: denominationConfigs,
                  fallbackDenominations: denominationConfigs,
                });
                const enabledDenominations = denominationConfigs.filter(
                  (denomination) => denomination?.enabled === true
                );
                const displayAmount = enabledDenominations.length > 0 ? enabledDenominations[0].denomination : 0;
                const isPercentage = String(clientConfigurationData?.earnPointDisplayType || "").toLowerCase() === "percentage";
                const earnDisplayType = isPercentage
                  ? EARNING_DISPLAY_TYPE.PERCENTAGE
                  : EARNING_DISPLAY_TYPE.VALUE;
                const earning = calculateEarning({
                  amount: displayAmount,
                  minEarnPoints: item?.minEarnPoints,
                  minBurnPoints: item?.maxEarnPoints,
                  baseDenomination,
                  displayType: earnDisplayType,
                  discountPercentage: item?.customerDiscount,
                });

                if (!(earning?.displayValue > 0)) {
                  return null;
                }

                return (
                  <>
                    <span className={"aapoForMob trend-mb m-b-2-5 percentageFixedEarnValue"}>
                      <Link
                        to="#"
                        data-bs-toggle="modal"
                        data-bs-target="#earnpoints"
                        className="searnBonusLabel"
                        onClick={(e) => {
                          e.preventDefault();
                          nav ? handleShowModal(item, index) : handleSubmit(item, index);
                        }}
                      >
                        {translation?.earn}{" "}
                        {earning.displayValue.toLocaleString("en-IN", {
                          maximumFractionDigits: 2,
                        })}
                        {isPercentage ? "%" : ` ${translation?.pts}`}
                        {" "}{translation?.earn_bonus_reward_productList}{" "}
                        <img src={pointIcon} alt="point" className="point_icon" />
                      </Link>
                    </span>
                    <span className={`aapoForMob trend-mb earn_upto_points_icon`}>
                      <Link
                        to="#"
                        data-bs-toggle="modal"
                        data-bs-target="#earnpoints"
                        className="earn-point1"
                        onClick={(e) => {
                          e.preventDefault();
                          nav ? handleShowModal(item, index) : handleSubmit(item, index);
                        }}
                      >
                        {translation?.buyAndEarnUpTo}{" "}

                        <span className="pts-col">
                          <img src={pointIcon} alt="point" className="point_icon" />{" "}
                          {calculatePointsern(item, true)} <span className="pts_text">{translation?.pts}</span>
                        </span>
                      </Link>
                    </span>
                  </>
                );
              }
              return null;
            })()}

            <button
              className="custom-button select-btn-fix" style={stockOutDenominations[index] ? { opacity: ".2", cursor: "not-allowed" } : {}}
              onClick={
                stockOutDenominations[index]
                  ? undefined
                  : () => handleSubmit(item, index)}
            >
              {translation?.select}
            </button>
          </div>
        </div>
      </div>
    ))
    : null;

  return (
    <>
      {productList}

      {/* Loader/Observer element for infinite scroll */}
      {hasMore && Array.isArray(product) && product.length > 0 && (
        <div
          ref={loaderRef}
          className="col-12 text-center py-4"
          style={{ minHeight: "50px" }}
        ></div>
      )}
    </>
  );
};

export default TrendingGiftCardOptimized;
